import { useState, useEffect, Suspense } from 'react';
import {
  Alert,
  Button,
  Title,
  Text,
  PasswordInput,
  Paper,
  Stack,
} from '@mantine/core';
import { AlertOctagonIcon } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { updateDoc, doc, collection } from 'firebase/firestore';

type FormState =
  | { status: 'idle'; password: string }
  | { status: 'registering'; password: string }
  | { status: 'success' }
  | { status: 'error'; message: string; password: string };

export const RegisterForm = () => {
  const [searchParams] = useSearchParams();
  const registrationPageId = searchParams.get('id');

  const pageLoadResult = useRegistrationPage(registrationPageId ?? undefined);
  const [formState, setFormState] = useState<FormState>({
    status: 'idle',
    password: '',
  });

  const navigate = useNavigate();

  const handleSubmit = async (props: {
    event: React.FormEvent<HTMLFormElement>;
    emailAddress: string;
    password: string;
    userFirestoreId: string;
    tenantId: string;
    firmId: string;
  }) => {
    props.event.preventDefault();
    if (
      formState.status === 'registering' ||
      formState.status === 'success' ||
      !pageLoadResult
    )
      return;

    setFormState({ status: 'registering', password: props.password });

    auth.tenantId = props.tenantId;
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        props.emailAddress,
        props.password,
      );
      await updateDoc(
        doc(
          collection(doc(collection(db, 'firms'), props.firmId), 'users'),
          props.userFirestoreId,
        ),
        {
          authId: userCredential.user.uid,
        },
      );

      setFormState({ status: 'success' });
      navigate('/');
    } catch (error) {
      console.error('Registration failed:', error);
      setFormState({
        status: 'error',
        message: 'Failed to register. Please try again.',
        password: props.password,
      });
    }
  };

  if (!registrationPageId) {
    return (
      <Suspense
        fallback={<LoadingView includeTitle={false} includeAnimation={true} />}
      >
        <NotFound />
      </Suspense>
    );
  }

  if (formState.status === 'success') {
    return (
      <>
        <Title order={1} ta='center'>
          Account created
        </Title>
        <div>
          <Alert color='green' radius='md'>
            Account created successfully. You should be redirected shortly.
          </Alert>
        </div>
      </>
    );
  }

  if (!pageLoadResult) {
    return <div>Loading...</div>;
  }

  if (pageLoadResult.status === 'error') {
    return (
      <div>
        <Alert color='red' radius='md'>
          {pageLoadResult.error}
        </Alert>
      </div>
    );
  }

  const isLoading = formState.status === 'registering';
  const errorMessage = formState.status === 'error' ? formState.message : '';

  return (
    <>
      <Title order={1} ta='center'>
        Create account
      </Title>
      <form
        onSubmit={(e) =>
          handleSubmit({
            event: e,
            emailAddress: pageLoadResult.payload.userEmail,
            password: formState.password,
            tenantId: pageLoadResult.payload.tenantId,
            firmId: pageLoadResult.payload.firmId,
            userFirestoreId: pageLoadResult.payload.userId,
          })
        }
      >
        <Stack gap='xl'>
          <Stack>
            <PasswordInput
              placeholder='Password'
              value={formState.password}
              onChange={(e) =>
                setFormState({ ...formState, password: e.target.value })
              }
              label='Password'
              size='md'
            />
            {errorMessage && (
              <Alert
                color='red'
                radius='md'
                icon={<AlertOctagonIcon size={16} />}
              >
                {errorMessage}
              </Alert>
            )}
          </Stack>
          <Button
            type='submit'
            loading={isLoading}
            disabled={isLoading}
            fullWidth
          >
            Continue
          </Button>
          <Text size='sm' ta='center'>
            Already have an account? <Link to={`/login`}>Sign in</Link>
          </Text>
        </Stack>
      </form>
    </>
  );
};

export default RegisterForm;

import {
  getEmployeeRegistrationPage,
  GetEmployeeRegistrationPageResult,
} from '@/cloudFunctions/auth';
import { auth, db } from '@/firebaseConfig';
import { LoadingView } from '@/components/LoadingView';
const NotFound = () => {
  return <div>Not Found</div>;
};

const useRegistrationPage = (registrationPageId: string | undefined) => {
  const [registrationPage, setRegistrationPage] =
    useState<GetEmployeeRegistrationPageResult>();

  useEffect(() => {
    const fetchRegistrationPage = async () => {
      if (!registrationPageId) {
        return;
      }
      const result = await getEmployeeRegistrationPage({
        registrationPageId,
      });
      setRegistrationPage(result);
    };
    fetchRegistrationPage();
  }, [registrationPageId]);

  return registrationPage;
};
